/**
 * Modal — a scrim over the screen with a surface card on it. Tapping the scrim
 * calls `onClose`; taps inside the card don't reach it.
 *
 * The three modals differ only in these values, so they're props:
 *
 *                  position  align    zIndex  scrim  card
 *   settings menu  absolute  top-end  20      55     260px, 8px 20px, shadow
 *   coming soon    fixed     center   30      70     300px, 24px, gap 16
 *   sign out       fixed     center   50      75     100% / max 345px, 24px, gap 14, shadow
 *
 * ModalCard is the card on its own, for a surface that floats without a scrim
 * (the project picker).
 */
import type { ComponentChildren, JSX } from "preact";

export interface ModalCardProps {
  width?: string;
  maxWidth?: string;
  padding: string;
  gap?: string;
  /** The overlay shadow (--mark-shadow-overlay). */
  shadow?: boolean;
  testid?: string;
  /** Placement for a card used on its own (e.g. absolute positioning). */
  style?: JSX.CSSProperties;
  onClick?: (e: MouseEvent) => void;
  children: ComponentChildren;
}

export function ModalCard({ width, maxWidth, padding, gap, shadow = false, testid, style, onClick, children }: ModalCardProps) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      style={{
        ...style,
        ...(width ? { width } : {}),
        ...(maxWidth ? { maxWidth } : {}),
        background: "var(--mark-bg-surface)",
        borderRadius: "16px",
        padding,
        display: "flex",
        flexDirection: "column",
        ...(gap ? { gap } : {}),
        ...(shadow ? { boxShadow: "var(--mark-shadow-overlay)" } : {}),
      }}
    >
      {children}
    </div>
  );
}

export interface ModalProps {
  onClose: () => void;
  /** fixed covers the viewport; absolute covers the nearest positioned parent. */
  position?: "fixed" | "absolute";
  /** center, or top-end (a menu dropping from a top-right control). */
  align?: "center" | "top-end";
  zIndex: number;
  /** Scrim opacity, one of the --mark-scrim-* tokens. */
  scrim: 55 | 70 | 75;
  /** Space between the screen edge and the card. */
  padding: string;
  testid?: string;
  /** Dialog semantics (role="dialog" aria-modal="true"). */
  dialog?: boolean;
  card: Omit<ModalCardProps, "children" | "onClick" | "style">;
  children: ComponentChildren;
}

export function Modal({
  onClose,
  position = "fixed",
  align = "center",
  zIndex,
  scrim,
  padding,
  testid,
  dialog = false,
  card,
  children,
}: ModalProps) {
  return (
    <div
      data-testid={testid}
      {...(dialog ? { role: "dialog", "aria-modal": "true" } : {})}
      onClick={onClose}
      style={{
        position,
        inset: 0,
        zIndex,
        display: "flex",
        alignItems: align === "center" ? "center" : "flex-start",
        justifyContent: align === "center" ? "center" : "flex-end",
        padding,
        background: `var(--mark-scrim-${scrim})`,
      }}
    >
      <ModalCard {...card} onClick={(e) => e.stopPropagation()}>
        {children}
      </ModalCard>
    </div>
  );
}
